import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import DetailRow from "../../pages/common-features/DetailRow";
import { useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import { camelToTitle } from "../ui/StepperDyanmicForm/helper";
import {
  isCompanyAddressField,
  isCompanyNameField,
  shouldShowCompanyAddress,
  sortCompanyAddressFields,
} from "./employmentSummaryHelper";

interface EmploymentDetailsTabProps {
  employmentDetails: any[];
  companyAddressDetails: any[];
  isMobile: boolean;
  designationsVal?: any;
}

const EmploymentDetailsTab: React.FC<EmploymentDetailsTabProps> = ({
  employmentDetails,
  companyAddressDetails,
  isMobile,
  designationsVal,
}) => {
  const { fetchUserApplicationData } = useSelector(
    (state: RootState) => state.stepper
  );

  // Helper to map fields from the vertical step to the required format
  function mapEmploymentDetailsFromVerticalStep(verticalStep: any) {
    if (!verticalStep || !verticalStep?.fields) return [];
    return verticalStep?.fields.map((fieldObj: any) => {
      const name = Object.keys(fieldObj)?.[0];
      const value = fieldObj?.[name];
      let label = name;
      // Remove 'Employment' prefix for employment details
      if (name.toLowerCase().startsWith("employment")) {
        label = label.replace(/^employment/, "");
      }
      // Mark if this is a company address field (Company Name stays in Employment Details)
      const isCompanyField = isCompanyAddressField({ name });
      // Remove 'Company' prefix for company address details
      if (isCompanyField && name.toLowerCase().includes("company")) {
        label = label.replace(/company/i, "");
      }
      // Capitalize each word and add space before trailing numbers
      label = label.replace(/([A-Z])/g, " $1").trim();
      label = label.replace(/(\D)(\d+)$/, "$1 $2");
      label = label.replace(/^./, (str) => str.toUpperCase());
      label = label
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
      return {
        label,
        name,
        value,
        isCompanyField,
      };
    });
  }

  let employmentDetailsToShow = employmentDetails;
  let companyAddressDetailsToShow = companyAddressDetails;
  if (
    fetchUserApplicationData &&
    fetchUserApplicationData?.partnerStartedJourney &&
    employmentDetailsToShow?.length === 0 &&
    companyAddressDetailsToShow?.length === 0
  ) {
    employmentDetailsToShow = employmentDetails || [];
    companyAddressDetailsToShow = companyAddressDetails || [];
  }

  // Designation is stored as a code (e.g. "ACR") on partner-started journeys too —
  // resolve it to its full label, mirroring the non-partner branch below.
  if (
    fetchUserApplicationData?.partnerStartedJourney &&
    Array.isArray(employmentDetailsToShow)
  ) {
    employmentDetailsToShow = employmentDetailsToShow.map((item: any) => {
      const isDesignation =
        item?.name === "designation" ||
        /designation$/i.test(String(item?.name || "")) ||
        String(item?.label || "").toLowerCase() === "designation";
      if (!isDesignation) return item;
      const fullName =
        designationsVal?.find((d: any) => d?.genDesignation === item.value)
          ?.genDesgDesc ?? item.value;
      return { ...item, value: fullName };
    });
  }

  if (
    fetchUserApplicationData &&
    !fetchUserApplicationData?.partnerStartedJourney
  ) {
    // Find the 'Applicant & Loan Details' horizontal step
    const applicantLoanStep = fetchUserApplicationData?.horizontalSteps?.find(
      (h: any) => h.horizontalStepName === "Applicant & Loan Details"
    );
    // Find the 'Employment Details' vertical step
    const employmentDetailsStep = applicantLoanStep?.verticalSteps?.find(
      (v: any) => v.verticalStepName === "Employment Details"
    );
    const allFields = mapEmploymentDetailsFromVerticalStep(
      employmentDetailsStep
    );
    employmentDetailsToShow = allFields?.filter(
      (field: any) => !field.isCompanyField
    );
    const code = allFields.find(
      (f:any) => f?.name === "employmentDesignation"
    )?.value;

  const fullName =
    designationsVal.find((d:any) => d?.genDesignation === code)?.genDesgDesc ?? code;

  employmentDetailsToShow = employmentDetailsToShow.map(item =>
    item.name === "employmentDesignation"
      ? { ...item, value: fullName }
      : item
  );
      companyAddressDetailsToShow = allFields?.filter(
        (field: any) => field.isCompanyField
      );
  }

  // Company Name is shown under Employment Details (partner API data may send it
  // with the company address), and the address follows a fixed order:
  // Address Line 1, 2, 3, Landmark, Pincode, City, State, Country
  const companyNameItems = (companyAddressDetailsToShow || []).filter(isCompanyNameField);
  if (companyNameItems.length > 0) {
    const hasCompanyName = (employmentDetailsToShow || []).some(isCompanyNameField);
    employmentDetailsToShow = hasCompanyName
      ? employmentDetailsToShow
      : [...(employmentDetailsToShow || []), ...companyNameItems];
    companyAddressDetailsToShow = (companyAddressDetailsToShow || []).filter(
      (field: any) => !isCompanyNameField(field)
    );
  }
  companyAddressDetailsToShow = sortCompanyAddressFields(companyAddressDetailsToShow);

  if (!employmentDetailsToShow || employmentDetailsToShow.length === 0) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );
  }

  const renderSectionHeading = (title: string, marginTop: number) => (
    <Grid item xs={12} sx={{ mt: marginTop, mb: 1 }}>
      <Box
        sx={{
          backgroundColor: "#edf6f6",
          borderLeft: "3px solid #00b3b5",
          borderRadius: "8px",
          px: 2,
          py: 1.25,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontSize: "15px", fontWeight: 600, color: "#6f6b7d" }}
        >
          {title}
        </Typography>
      </Box>
    </Grid>
  );
  // Matches isEmployed / employmentType by name suffix, so it works for both the
  // partner API names (isEmployed) and the form names (employmentisEmployed)
  const showCompanyAddress = shouldShowCompanyAddress(employmentDetailsToShow);
  return (
    <>
      {/* {renderSectionHeading("Employment Details", 2)} */}
      {employmentDetailsToShow?.map((field: any, subIndex: number) => (
        field.value!="" && field.value!=null && field.value!=undefined &&
        <Grid item xs={12} sm={6} md={3} key={subIndex}>
          <DetailRow
            item={{
              label: field.label,
              value:
                typeof field.value === "boolean"
                  ? field.value
                    ? "Yes"
                    : "No"
                  : typeof field.value === "string" &&
                      ["true", "false"].includes(field.value?.toLowerCase?.())
                    ? field.value.toLowerCase() === "true"
                      ? "Yes"
                      : "No"
                    : field.value,
              isVerified: false,
            }}
          />
        </Grid>
      ))}

      {showCompanyAddress && renderSectionHeading("Company Address", isMobile ? 5 : 4)}
      {showCompanyAddress && companyAddressDetailsToShow?.map((field: any, subIndex: number) => (
        field.value!="" && field.value!=null && field.value!=undefined &&
        <Grid item xs={12} sm={6} md={3} key={subIndex}>
          <DetailRow
            item={{ label: field.label, value: field.value, isVerified: false }}
          />
        </Grid>
      ))}
    </>
  );
};

export default EmploymentDetailsTab;
